import { getUserAvatarInfos } from 'helpers/clients';

import { OAUTH_ERROR, SET_LOGIN_FORWARD_URL, SET_ANONYMOUS_USER_DATA, FORCE_LOGOUT } from 'actions/LoginActions';

const clearImpersonationSideEffects = () => {
  if (typeof window !== 'undefined') {
    window.isImpersonating = false;
  }
  if (typeof window !== 'undefined' && window.sessionStorage) {
    window.sessionStorage.removeItem('impersonationAccessToken');
    window.sessionStorage.removeItem('impersonationUser');
  }
};

const initialState = {
  user: {
    logged: false,
    adhocLogout: false,
    loginPending: false,
    logoutPending: false,
    loginError: false,
    loginErrors: [],
    anonymousEmail: localStorage.getItem('anonymousEmail'),
    anonymousName: localStorage.getItem('anonymousName'),
    voipDeviceToken: '',
  },
  forwardUrl: null,
};

// export function auth(state = {}, action) {
//   if (['FETCH_CREDENTIALS_FULFILLED', 'HANDLE_LOGIN_FULFILLED'].includes(action.type)) {
//     const { data } = action.payload;

//     if (data === '') {
//       return {
//         ...state,
//         logged: false,
//         loginPending: false,
//         logoutPending: false,
//         loginError: false,
//         loginErrors: [],
//       };
//     }

//     data.profile = {
//       ...data.profile,
//       ...getUserAvatarInfos(data),
//     };

//     return {
//       ...state,
//       ...data,
//       logged: true,
//       loginPending: false,
//       logoutPending: false,
//       loginError: false,
//       loginErrors: [],
//     };
//   }
//   return state;
// }

export default function login(state = initialState, action) {
  switch (action.type) {
    case 'HANDLE_LOGIN_PENDING':
    case 'FETCH_CREDENTIALS_PENDING': {
      return {
        ...state,
        user: {
          ...state.user,
          logged: false,
          adhocLogout: false,
          loginPending: true,
          logoutPending: false,
          loginError: false,
          loginErrors: [],
        },
      };
    }

    case 'HANDLE_LOGIN_REJECTED':
    case 'FETCH_CREDENTIALS_REJECTED': {
      // this action should never happen (FETCH_CREDENTIALS_REJECTED)
      return {
        ...state,
        user: {
          ...state.user,
          logged: false,
          adhocLogout: false,
          loginPending: false,
          logoutPending: false,
          loginError: true,
          loginErrors: action.payload.response.data,
        },
      };
    }

    /* we fetch the credentials after the website was reloaded */
    /* we also get user data after the user logged in */
    case 'HANDLE_LOGIN_FULFILLED':
    case 'FETCH_CREDENTIALS_FULFILLED': {
      console.log('[Login Reducer] FETCH_CREDENTIALS_FULFILLED received', {
        hasPayload: !!action.payload,
        payloadType: typeof action.payload,
        hasData: !!(action.payload && action.payload.data),
        dataValue: action.payload?.data,
        dataType: typeof action.payload?.data,
      });

      const { data } = action.payload || {};

      // Handle empty data - this should reset loginPending to false
      if (data === '' || data === null || data === undefined || (typeof data !== 'object')) {
        console.log('[Login Reducer] FETCH_CREDENTIALS_FULFILLED with empty/invalid data - setting loginPending: false', {
          currentLoginPending: state.user.loginPending,
        });
        const newState = {
          ...state,
          user: {
            ...state.user,
            logged: false,
            adhocLogout: false,
            loginPending: false,
            logoutPending: false,
            loginError: false,
            loginErrors: [],
          },
        };
        console.log('[Login Reducer] New state loginPending:', newState.user.loginPending);
        return newState;
      }

      // Ensure profile exists before marking as logged in
      // Note: first_name and last_name are optional - users can have empty names
      // The backend allows creating users without names (e.g., via Alzura OAuth)
      if (!data.profile) {
        console.warn('User data incomplete - missing profile object', {
          hasProfile: !!data.profile,
          dataKeys: data ? Object.keys(data) : [],
        });
        return {
          ...state,
          user: {
            ...state.user,
            logged: false,
            adhocLogout: false,
            loginPending: false,
            logoutPending: false,
            loginError: false,
            loginErrors: [],
          },
        };
      }

      // Ensure profile exists before adding avatar info
      if (data && data.profile) {
      data.profile = {
        ...data.profile,
        ...getUserAvatarInfos(data),
      };
      }

      return {
        ...state,
        user: {
          ...data,
          logged: true,
          adhocLogout: false,
          loginPending: false,
          logoutPending: false,
          loginError: false,
          loginErrors: [],
        },
      };
    }

    case 'HANDLE_LOGOUT_PENDING': {
      return {
        ...state,
        user: {
          ...state.user,
          logged: false,
          adhocLogout: false,
          loginPending: false,
          logoutPending: true,
          loginError: false,
          loginErrors: [],
        },
      };
    }

    case 'HANDLE_LOGOUT_REJECTED': {
      break;
    }

    case 'HANDLE_LOGOUT_FULFILLED': {
      clearImpersonationSideEffects();
      return {
        ...state,
        user: {
          // Reset to clean initial state, only keeping anonymous user fields from localStorage
          logged: false,
          adhocLogout: false,
          loginPending: false,
          logoutPending: false,
          loginError: false,
          loginErrors: [],
          anonymousEmail: localStorage.getItem('anonymousEmail'),
          anonymousName: localStorage.getItem('anonymousName'),
          voipDeviceToken: '',
        },
      };
    }

    case OAUTH_ERROR: {
      return {
        ...state,
        user: { ...state.user, loginError: true, loginErrors: [action.payload.error] },
      };
    }

    case 'FORM_FINISHED': {
      if (action.payload.formName === 'profileEdit') {
        const user = { ...state.user, ...action.payload.response.data };
        return { ...state, user };
      }

      if (action.payload.formName === 'forgotPasswordReset') {
        const user = { ...state.user, ...action.payload.response.data, logged: true };
        return { ...state, user };
      }

      if (action.payload.formName === 'checkoutSignupForm') {
        const user = { ...state.user, ...action.payload.response.data, logged: true };
        return { ...state, user };
      }

      if (action.payload.formName === 'clientAcceptTerms') {
        const user = { ...state.user, has_accepted_terms_and_conditions: true };
        return { ...state, user };
      }

      if (action.payload.formName === 'clientAcceptTermsOfUse') {
        const user = { ...state.user, has_accepted_terms_of_use: true };
        return { ...state, user };
      }

      if (action.payload.formName === 'clientAcceptOrderProcessingContract') {
        const user = { ...state.user, has_accepted_order_processing_contract: true };
        return { ...state, user };
      }

      if (action.payload.formName === 'initSoleRepresentative') {
        const user = { ...state.user, ...action.payload.response.data };
        return { ...state, user };
      }

      break;
    }

    case 'UPLOAD_FILE_FINISHED': {
      if (
        action.payload.response &&
        action.payload.response.data &&
        action.payload.response.data !== undefined &&
        action.payload.response.data.email !== undefined &&
        state.user.profile.id === action.payload.response.data.id
      ) {
        const user = { ...state.user };
        user.profile = action.payload.response.data;

        return { ...state, user };
      }

      break;
    }

    case SET_LOGIN_FORWARD_URL: {
      return { ...state, forwardUrl: action.payload };
    }

    case SET_ANONYMOUS_USER_DATA: {
      const { email: anonymousEmail, name: anonymousName } = action.payload;
      return { ...state, user: { ...state.user, anonymousEmail, anonymousName } };
    }

    case FORCE_LOGOUT: {
      clearImpersonationSideEffects();
      return {
        ...state,
        user: {
          // Reset to clean initial state, only keeping anonymous user fields from localStorage
          logged: false,
          adhocLogout: true, // Mark as forced logout for UI feedback
          loginPending: false,
          logoutPending: false,
          loginError: false,
          loginErrors: [],
          anonymousEmail: localStorage.getItem('anonymousEmail'),
          anonymousName: localStorage.getItem('anonymousName'),
          voipDeviceToken: '',
        },
      };
    }

    // Handle subscription activation to update user subscriptions immediately
    case 'ACTIVATE_SUBSCRIPTION_PRICE_FOR_USER_FULFILLED': {
      if (!state.user.logged || !action.payload?.list?.[0]) {
        return state;
      }

      const newSubscription = action.payload.list[0];
      const currentSubscriptions = state.user.subscriptions || [];

      // Add the new subscription to user's subscriptions
      const updatedSubscriptions = [...currentSubscriptions, newSubscription];

      return {
        ...state,
        user: {
          ...state.user,
          subscriptions: updatedSubscriptions,
        },
      };
    }

    // Handle subscription cancellation to update user subscriptions immediately
    case 'CANCEL_SUBSCRIPTION_PRICE_FOR_USER_FULFILLED': {
      if (!state.user.logged || !action.payload?.id) {
        return state;
      }

      const cancelledSubscriptionId = action.payload.id;
      const currentSubscriptions = state.user.subscriptions || [];

      // Remove the cancelled subscription from user's subscriptions
      const updatedSubscriptions = currentSubscriptions.filter(
        sub => sub.id !== cancelledSubscriptionId
      );

      return {
        ...state,
        user: {
          ...state.user,
          subscriptions: updatedSubscriptions,
        },
      };
    }

    // Handle subscription editing to update user subscriptions immediately
    case 'EDIT_SUBSCRIPTION_PRICE_FOR_USER_FULFILLED': {
      if (!state.user.logged || !action.payload?.list?.[0]) {
        return state;
      }

      const updatedSubscription = action.payload.list[0];
      const currentSubscriptions = state.user.subscriptions || [];

      // Update the existing subscription in user's subscriptions
      const updatedSubscriptions = currentSubscriptions.map(sub =>
        sub.id === updatedSubscription.id ? updatedSubscription : sub
      );

      return {
        ...state,
        user: {
          ...state.user,
          subscriptions: updatedSubscriptions,
        },
      };
    }
  }

  if (action.type.indexOf('REJECTED') !== -1) {
    /** status 401 unauthorized
     *  status 403 forbidden
     */
    if (action.payload && action.payload.response && (action.payload.response.status === 403 || action.payload.response.status === 401)) {
      const user = {
        ...state.user,
        logged: false,
        adhocLogout: true,
        loginPending: false,
        logoutPending: false,
        loginError: false,
        loginErrors: [],
      };
      return { ...state, user };
    }
  }

  return state;
}
